import React, {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { Question, Topic, Intake, questionApi } from "./api";
import "./questions.css";
import QuestionEngagement from "./QuestionEngagement";
import LocationFields from "./LocationFields";
import {
  Location,
  fromJurisdiction,
  sameLocation,
  toJurisdiction,
} from "./intake";
import WorkproductPage from "../WorkProductPage/WorkproductPage";
import { usePortal } from "../Portal/PortalContext";
import { customerNeedsReply } from "../Reviews/customerWorkflow";
export default function QuestionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const firstMessage = location.state?.firstMessage;
  // A short AI-written title replaces the intake title once, for a new question.
  const [aiTitle, setAiTitle] = useState<string | null>(null);
  const titleRequested = useRef<string | null>(null);
  useEffect(() => {
    setAiTitle(null);
    const text =
      id && firstMessage?.questionId === id ? firstMessage.text : null;
    if (!id || !text || titleRequested.current === id) return;
    titleRequested.current = id;
    Promise.resolve()
      .then(() =>
        questionApi<{ question: Question }>(`/${id}/title`, "POST", {
          message: text,
        })
      )
      .then(d => {
        if (d?.question?.title) setAiTitle(d.question.title);
      })
      .catch(() => {});
    // Only a new question's first message asks for a title.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const paymentsEnabled = Boolean(usePortal().access?.paymentsEnabled);
  const [reloadQuestion, setReloadQuestion] = useState(0);
  const [record, setRecord] = useState<Question | null>(null),
    [topics, setTopics] = useState<Topic[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false),
    [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(""),
    [topic, setTopic] = useState("other"),
    [place, setPlace] = useState<Location>({ country: "", region: "" });
  const [terms, setTerms] = useState<any>(null),
    [accepted, setAccepted] = useState(false);
  const creationKey = useRef(crypto.randomUUID());
  // Reviews come from the question list endpoint, which carries review history.
  const [reviews, setReviews] = useState<Question["reviews"]>(undefined);
  const updateReview = useCallback((review: any) => {
    setReviews(current =>
      current?.map(r =>
        r.id === review.id
          ? { ...r, ...review, needs_reply: customerNeedsReply(review) }
          : r
      )
    );
  }, []);
  const [reviewLoadError, setReviewLoadError] = useState("");
  const reviewLoadSequence = useRef(0);
  const currentQuestionId = useRef(id);
  currentQuestionId.current = id;
  useEffect(() => {
    reviewLoadSequence.current++;
    setReviews(undefined);
    setReviewLoadError("");
    return () => {
      reviewLoadSequence.current++;
    };
  }, [id]);
  const loadReviews = useCallback(() => {
    if (!id) return;
    setReviewLoadError("");
    const sequence = ++reviewLoadSequence.current;
    const current = () =>
      sequence === reviewLoadSequence.current &&
      id === currentQuestionId.current;
    Promise.resolve()
      .then(() =>
        questionApi<{ questions: Question[] }>(
          `?status=all&filter=all&questionId=${encodeURIComponent(id)}`
        )
      )
      .then(d => {
        if (!current()) return;
        if (!d.questions[0]) {
          setReviewLoadError(
            "We couldn’t load your review history. Retry before requesting another review."
          );
          return;
        }
        setReviews(old => {
          const next = d.questions[0].reviews || [];
          return JSON.stringify(old) === JSON.stringify(next) ? old : next;
        });
      })
      .catch(
        () =>
          current() &&
          setReviewLoadError(
            "We couldn’t load your review history. Retry before requesting another review."
          )
      );
  }, [id]);
  const active = record?.status === "active";
  useEffect(() => {
    if (!active) return;
    loadReviews();
    const refresh = () => {
      if (document.visibilityState === "visible") loadReviews();
    };
    window.addEventListener("focus", refresh);
    const timer = setInterval(refresh, 30000);
    return () => {
      window.removeEventListener("focus", refresh);
      clearInterval(timer);
    };
  }, [active, loadReviews, location.search]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setSaved(false);
    setRecord(null);
    setReviews(undefined);
    setReviewLoadError("");
    setTitle("");
    setTopic("other");
    setPlace({ country: "", region: "" });
    creationKey.current = crypto.randomUUID();
    Promise.all([
      questionApi<{ topics: Topic[]; terms?: any }>(
        "/options",
        "GET",
        undefined,
        controller.signal
      ),
      id
        ? questionApi<{ question: Question }>(
            `/${id}`,
            "GET",
            undefined,
            controller.signal
          )
        : Promise.resolve(null),
    ])
      .then(([options, data]) => {
        setTopics(options.topics);
        setTerms(options.terms || null);
        setAccepted(false);
        if (data) {
          const q = data.question;
          setRecord(q);
          setTitle(q.title);
          setTopic(q.topicKey);
          setPlace(fromJurisdiction(q.jurisdiction));
        }
      })
      .catch(e => {
        if (!controller.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [id, reloadQuestion]);
  // An unchanged place keeps the level an older intake stored for it.
  const locationChanged =
    !record || !sameLocation(place, fromJurisdiction(record.jurisdiction));
  function intake(): Intake {
    return {
      title,
      topicKey: topic,
      jurisdiction:
        !locationChanged && record && topic === record.topicKey
          ? record.jurisdiction
          : toJurisdiction(topic, place),
    };
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const data = await questionApi<{ question: Question }>(
        id ? `/${id}` : "",
        id ? "PUT" : "POST",
        {
          ...intake(),
          ...(record
            ? { version: record.version }
            : { creationKey: creationKey.current }),
        }
      );
      setRecord(data.question);
      setSaved(true);
      if (!id) {
        if (terms && accepted) {
          try {
            await questionApi(`/${data.question.id}/activate`, "POST", {
              version: data.question.version,
              termsVersion: terms.version,
              termsHash: terms.hash,
              accepted: true,
            });
          } catch (e: any) {
            navigate(`/questions/${data.question.id}`, {
              replace: true,
              state: {
                activationError: `Your draft was saved, but the AI conversation could not start. Try again below. ${e.message}`,
              },
            });
            return;
          }
        }
        navigate(`/questions/${data.question.id}`, { replace: true });
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function changeStatus() {
    if (!record || busy) return;
    setBusy(true);
    setError("");
    try {
      const data = await questionApi<{ question: Question }>(
        `/${record.id}/status`,
        "PATCH",
        {
          status: record.status === "draft" ? "archived" : "draft",
          version: record.version,
        }
      );
      setRecord(data.question);
      navigate("/questions");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const archived = record?.status === "archived";
  const dirty = record
    ? title !== record.title || topic !== record.topicKey || locationChanged
    : false;
  if (loading)
    return (
      <main className="reviewed-documents" role="status">
        <Spinner size="sm" /> Opening question…
      </main>
    );
  if (id && !record)
    return (
      <main className="reviewed-documents">
        <Alert variant="danger" role="alert">
          {error || "Question not found"}
        </Alert>
        <Button
          variant="outline-dark"
          className="me-3"
          onClick={() => setReloadQuestion(n => n + 1)}
        >
          Retry question
        </Button>
        <Link to="/questions">Back to questions</Link>
      </main>
    );
  if (record?.status === "active" && record.workproductId)
    return (
      <WorkproductPage
        workspaceId={record.workproductId}
        question={record}
        topics={topics}
        onQuestionUpdated={setRecord}
        reviews={reviews}
        reviewLoadError={reviewLoadError}
        title={aiTitle || undefined}
        onReviewsChanged={loadReviews}
        onReviewUpdated={updateReview}
        initialMessage={
          firstMessage?.questionId === id ? firstMessage.text : undefined
        }
        onInitialMessageConsumed={() =>
          navigate(location.pathname, { replace: true, state: null })
        }
      />
    );
  return (
    <main className="reviewed-documents">
      <Link className="question-back" to="/questions">
        ← My questions
      </Link>
      <header className="reviewed-documents-heading">
        <div>
          <p className="reviewed-eyebrow">{archived ? "ARCHIVED" : "DRAFT"}</p>
          <h1>{id ? "Your question" : "Ask a question"}</h1>
          <p>Choose a topic and where it applies, then start the AI chat.</p>
        </div>
      </header>
      <div className="question-layout">
        <section>
          {(error || location.state?.activationError) && (
            <Alert role="alert" variant="danger">
              {error || location.state?.activationError}
            </Alert>
          )}
          {saved && (
            <Alert role="status" variant="success">
              Draft saved.
            </Alert>
          )}
          <Form onSubmit={save} onChange={() => setSaved(false)}>
            <fieldset disabled={busy || archived}>
              <Form.Group className="mb-4" controlId="question-title">
                <Form.Label>Question title</Form.Label>
                <Form.Control
                  required
                  maxLength={255}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="For example, Employment agreement"
                />
              </Form.Group>
              <Form.Group className="mb-4" controlId="question-topic">
                <Form.Label>Area of law</Form.Label>
                <Form.Select
                  value={topic}
                  onChange={e => setTopic(e.target.value)}
                >
                  {topics.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="question-location-fields mb-4">
                <LocationFields
                  idPrefix="question"
                  value={place}
                  onChange={setPlace}
                />
              </div>
              {!id && terms && (
                <section className="question-engagement">
                  <h2>{terms.title}</h2>
                  <p>{terms.text}</p>
                  <Form.Check
                    id="new-question-acceptance"
                    checked={accepted}
                    onChange={e => setAccepted(e.target.checked)}
                    label="I have read and accept this test-beta notice."
                  />
                </section>
              )}
              {!archived && (
                <Button
                  type="submit"
                  disabled={
                    busy || !title.trim() || (!id && !!terms && !accepted)
                  }
                >
                  {busy
                    ? "Saving…"
                    : id
                      ? "Save changes"
                      : terms
                        ? "Start AI conversation"
                        : "Save draft"}
                </Button>
              )}
            </fieldset>
          </Form>
          {record?.questionText && (
            <details className="mt-3">
              <summary>Previously saved intake note</summary>
              <p style={{ whiteSpace: "pre-wrap" }}>{record.questionText}</p>
            </details>
          )}
          {record && (
            <Button
              variant="link"
              className="mt-3"
              disabled={busy || (!archived && dirty)}
              onClick={changeStatus}
            >
              {archived
                ? record.workproductId
                  ? "Restore conversation"
                  : "Restore draft"
                : "Archive draft"}
            </Button>
          )}
          {record && !archived && dirty && (
            <p className="text-secondary mt-2">
              Save your changes before archiving this draft.
            </p>
          )}
        </section>
        <aside className="question-next">
          {record && !archived && (
            <QuestionEngagement
              question={record}
              dirty={dirty}
              onActivated={async () => {
                const data = await questionApi<{ question: Question }>(
                  `/${record.id}`
                );
                setRecord(data.question);
              }}
            />
          )}
          <h2>What happens next?</h2>
          <ol>
            <li>Choose a title, topic, and where it applies.</li>
            <li>Accept the terms.</li>
            <li>Ask the AI your question. AI answers are free.</li>
            <li>When it matters, get an attorney review.</li>
          </ol>
          <p className="question-availability">
            {paymentsEnabled
              ? "You pay by card only after an attorney accepts the agreed scope and price."
              : "Beta test — no charge will be made."}
          </p>
        </aside>
      </div>
    </main>
  );
}
